<template>
  <v-container class="login-container d-flex align-center justify-center fill-height">
    <v-card class="pa-5 login-card">
      <div class="d-flex justify-space-between align-center">
        <v-img
            alt="Hitachi Logo"
            class="ma-5"
            contain
            max-width="50%"
            src="/hitachi-logo.png"
        />
        <v-img
            alt="NAT Logo"
            class="ma-5"
            contain
            max-width="50%"
            src="/NAT_logo.png"
        />
      </div>

      <v-card-text>
        <v-form @submit.prevent>
          <div class="timeline">
            <!-- Step 1 -->
            <div class="step">
              <div class="dot ok">
                <v-icon size="18">mdi-check</v-icon>
              </div>
              <div class="content">
                <div class="title">1 - Login to Backend</div>
                <div class="subtitle text-success">OK</div>
              </div>
            </div>

            <!-- Step 2 -->
            <div class="step">
              <div class="dot" :class="step2Class">
                <v-icon v-if="sdkReady" size="18">mdi-check</v-icon>
                <v-progress-circular
                    v-else-if="sdkInitializing"
                    indeterminate
                    size="18"
                    width="2"
                />
                <v-icon v-else size="18">mdi-close</v-icon>
              </div>

              <div class="content">
                <div class="title">2 - SDK ready</div>
                <div class="subtitle" :class="sdkSubtitleClass">
                  <span v-if="sdkReady">SDK ready</span>
                  <span v-else-if="sdkInitializing">Initializing SDK...</span>
                  <span v-else>{{ sdkDesc }}</span>
                </div>
              </div>
            </div>

            <!-- Step 3 -->
            <div class="step">
              <div class="dot" :class="step3Class">
                <v-icon v-if="step3Done" size="18">mdi-check</v-icon>
                <v-progress-circular
                    v-else-if="step3Loading"
                    indeterminate
                    size="18"
                    width="2"
                />
                <v-icon v-else-if="step3Error" size="18">mdi-close</v-icon>
                <v-icon v-else size="18">mdi-minus</v-icon>
              </div>

              <div class="content">
                <div class="title">3 - Login to LTE server</div>

                <div class="subtitle" :class="step3Error ? 'text-error' : ''">
                  <span v-if="step3Done" class="text-success">Ready</span>
                  <span v-else-if="step3Loading">Fetching credentials...</span>
                  <span v-else-if="step3Error">{{ step3ErrorMsg }}</span>
                  <span v-else>Waiting for SDK ready</span>
                </div>
              </div>
            </div>
          </div>

          <v-btn
              block
              class="mt-6"
              color="#4c4c4c"
              type="button"
              :disabled="primaryDisabled"
              @click="onPrimaryClick"
          >
            {{ primaryLabel }}
          </v-btn>

          <v-btn
              block
              class="mt-3"
              color="#4c4c4c"
              type="button"
              @click="keycloak.logout()"
          >
            Back
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useAuthStore } from "@/stores/authStores";
import { useICPStore } from "@/stores/icpStore";
import api from "@/services/api";
import { keycloak } from "@/main";

const authStore = useAuthStore();
const icpStore = useICPStore();

const step3Loading = ref(false);
const step3Done = ref(false); // now means "credentials fetched and validated"
const step3Error = ref(false);
const step3ErrorMsg = ref("");

const sdkReady = computed(() => icpStore.eventData?.status === "0");
const sdkInitializing = computed(() => !icpStore.eventData);
const sdkDesc = computed(() => icpStore.eventData?.desc ?? "");
const sdkError = computed(() => !!icpStore.eventData && icpStore.eventData.status !== "0");

// credentials (prepared, but login not executed automatically)
const preparedCreds = ref(null);

// button label/behaviour
const canContinue = computed(() => sdkReady.value && step3Done.value && !step3Loading.value && !step3Error.value);
const primaryLabel = computed(() => (canContinue.value ? "Continue to dispatcher" : "Retry"));

const primaryDisabled = computed(() => step3Loading.value); // allow click even if sdk not ready (it will retry step3 preflight)

// UI classes
const step2Class = computed(() => {
  if (sdkReady.value) return "ok";
  if (sdkInitializing.value) return "loading";
  return "bad";
});

const sdkSubtitleClass = computed(() => {
  if (sdkReady.value) return "text-success";
  if (sdkError.value) return "text-error";
  return "";
});

const step3Class = computed(() => {
  if (step3Done.value) return "ok";
  if (step3Loading.value) return "loading";
  if (step3Error.value) return "bad";
  return "idle";
});

let started = false;

function humanError(err) {
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (typeof data === "string" && data.trim()) return status ? `${status} - ${data}` : data;
  if (data && typeof data === "object") {
    const msg = data.message || data.error || JSON.stringify(data);
    return status ? `${status} - ${msg}` : msg;
  }
  return err?.message || "Something went wrong";
}

async function getWorkstationIP() {
  if (!window.config?.get) throw new Error("Electron config not available.");
  const ip = await window.config.get("currentWorkstationIP");
  if (!ip) throw new Error("Missing currentWorkstationIP in config.");
  return ip;
}

async function fetchCredentials(workstationIp) {
  const { data } = await api.get("mcx/getWorkstationCredentials", {
    params: { workstationIp },
  });
  return data;
}

/**
 * Step 3 PREP: fetch + validate credentials, but DO NOT login.
 * This runs only when SDK ready, and can be retried by user.
 */
async function prepareLogin() {
  if (started || !sdkReady.value) return;

  started = true;
  step3Loading.value = true;
  step3Done.value = false;
  step3Error.value = false;
  step3ErrorMsg.value = "";
  preparedCreds.value = null;

  try {
    const ip = await getWorkstationIP();
    const creds = await fetchCredentials(ip);

    if (!creds?.username || !creds?.password) {
      throw new Error("Backend returned invalid credentials.");
    }

    preparedCreds.value = {
      username: creds.username,
      password: creds.password,
      operatorProfile: creds.operatorProfile,
    };

    step3Done.value = true; // ready to continue
  } catch (err) {
    console.error(err);
    step3Error.value = true;
    step3ErrorMsg.value = humanError(err);
    started = false; // allow retry
  } finally {
    step3Loading.value = false;
  }
}

/**
 * Final action: do the actual login only when user clicks "Continue to dispatcher".
 */
async function continueToDispatcher() {
  if (!canContinue.value || !preparedCreds.value) return;

  step3Loading.value = true;
  step3Error.value = false;
  step3ErrorMsg.value = "";

  try {
    await authStore.login(
        preparedCreds.value.username,
        preparedCreds.value.password,
        preparedCreds.value.operatorProfile
    );
    // if login succeeds, user will be routed by your auth flow/store as before
  } catch (err) {
    console.error(err);
    step3Error.value = true;
    step3ErrorMsg.value = humanError(err);
    // allow retry (re-prepare or continue again)
  } finally {
    step3Loading.value = false;
  }
}

function onPrimaryClick() {
  if (canContinue.value) {
    continueToDispatcher();
  } else {
    // retry preparation when not ready
    started = false;
    prepareLogin();
  }
}

watch(sdkReady, (val) => {
  // No automatic authStore.login anymore.
  // When SDK becomes ready, we automatically prepare credentials once,
  // then user must click "Continue to dispatcher".
  if (val) {
    started = false;
    prepareLogin();
  }
});

onMounted(() => {
  started = false;
  // don't prepare until SDK is ready (prepareLogin checks sdkReady anyway)
  prepareLogin();
});
</script>

<style scoped>
.login-container {
  height: 100% !important;
  background-color: #f5f5f5;
  max-width: 100% !important;
}

.login-card {
  width: 33vw !important;
  height: 90vh !important;
  overflow-y: auto;
}

.timeline {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 20px;
  position: relative;
}

.timeline::before {
  content: "";
  position: absolute;
  top: 14px;
  left: 8%;
  right: 8%;
  height: 2px;
  background-color: #d0d0d0;
  z-index: 0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 30%;
  position: relative;
  z-index: 1;
}

.dot {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 28px;
  border: 2px solid #cfcfcf;
  background: white;
  margin-bottom: 8px;
}

.dot.ok {
  border-color: #2e7d32;
  background: #2e7d32;
  color: white;
}

.dot.bad {
  border-color: #c62828;
  background: #c62828;
  color: white;
}

.dot.loading {
  border-color: #9e9e9e;
  background: #ffffff;
  color: #666;
}

.dot.idle {
  border-color: #cfcfcf;
  background: #ffffff;
  color: #999;
}

.content .title {
  font-weight: 600;
  font-size: 0.95rem;
}

.content .subtitle {
  font-size: 0.85rem;
  margin-top: 4px;
  opacity: 0.95;
}
</style>
